import { formatDuration } from '@butter/library/datetime'
import type { KeyPoint } from '@lgc/database/types/domains/subscription'

/**
 * deriveKeyBenefits
 *
 * The single canonical fallback for a subscription's key benefits, inferred
 * from its configuration. Used across mobile, web, and the admin preview so
 * every surface shows the SAME "faked" benefits when an admin hasn't authored
 * any. Replaces the previously divergent `getIncludedItems` (mobile, title-only)
 * and `membershipDetails` (web, title+description) inline implementations.
 *
 * Callers pick between authored benefits and this fallback:
 *   `keyBenefits.length ? keyBenefits : deriveKeyBenefits(plan)`
 *
 * The input is a minimal structural shape that `Membership`, `Pass`,
 * `FormattedSubscriptionPlan`, and the admin form-state adapter all satisfy.
 * All fields are optional except `subscriptionType`; absent fields skip their
 * line. There is intentionally no free-text description fallback — a degenerate
 * plan simply yields `[]`, which `KeyBenefitsList hideWhenEmpty` collapses.
 */
export interface DeriveKeyBenefitsInput {
  subscriptionType: 'membership' | 'pass'
  maxMinutesPerDay?: number | null
  maxMinutesPerWeek?: number | null
  maxMinutesPerMonth?: number | null
  maxActiveMinutes?: number | null
  maxTotalMinutes?: number | null
  billingFrequency?: string | null
  neverEnds?: boolean | null
  validityDays?: number | null
  /** Concurrent-session cap — a COUNT, not minutes. */
  maxActiveSessions?: number | null
}

export function deriveKeyBenefits(input: DeriveKeyBenefitsInput): KeyPoint[] {
  const out: KeyPoint[] = []
  const isMembership = input.subscriptionType === 'membership'

  // Daily access — memberships surface an explicit "unlimited" line; passes are
  // bounded by validity/total, so a null daily cap isn't a headline benefit.
  if (input.maxMinutesPerDay == null) {
    if (isMembership) {
      out.push({
        title: 'Unlimited daily access',
        description: 'Book and use any available bay without daily time limits',
      })
    }
  } else {
    const label = formatDuration(input.maxMinutesPerDay)
    out.push({ title: `${label} per day`, description: `Book up to ${label} of bay time each day` })
  }

  if (input.maxMinutesPerWeek != null) {
    const label = formatDuration(input.maxMinutesPerWeek)
    out.push({ title: `${label} per week`, description: `Weekly booking allowance of ${label}` })
  }

  if (input.maxMinutesPerMonth != null) {
    const label = formatDuration(input.maxMinutesPerMonth)
    out.push({ title: `${label} per month`, description: `Monthly booking allowance of ${label}` })
  }

  if (input.maxActiveMinutes != null) {
    const label = formatDuration(input.maxActiveMinutes)
    out.push({
      title: `${label} in active bookings`,
      description: `Keep up to ${label} of upcoming bookings at once`,
    })
  }

  if (input.maxActiveSessions != null) {
    const n = input.maxActiveSessions
    const label = `${n} active ${n === 1 ? 'session' : 'sessions'}`
    out.push({
      title: label,
      description: `Keep up to ${label} on the calendar at once, any length`,
    })
  }

  if (isMembership) {
    if (input.billingFrequency) {
      const freq = input.billingFrequency.charAt(0).toUpperCase() + input.billingFrequency.slice(1)
      out.push({ title: `${freq} billing`, description: `Your membership is billed ${input.billingFrequency}` })
    }
    if (input.neverEnds) {
      out.push({ title: 'No expiration', description: 'Your membership stays active with no end date' })
    }
  } else {
    if (input.validityDays != null) {
      const d = input.validityDays
      out.push({
        title: `${d} ${d === 1 ? 'day' : 'days'} validity`,
        description: `Use this pass within ${d} ${d === 1 ? 'day' : 'days'} of activation`,
      })
    }
    if (input.maxTotalMinutes != null) {
      const label = formatDuration(input.maxTotalMinutes)
      out.push({ title: `${label} total play time`, description: `This pass includes ${label} of bay time` })
    }
  }

  return out
}
